import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { syncEnabled } from '../config';
import * as cognito from './cognito';
import type { AuthUser } from './cognito';

type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  /** Whether a backend is configured at all — gates all account UI. */
  enabled: boolean;
  status: AuthStatus;
  user: AuthUser | null;
  signUp: (
    email: string,
    password: string,
    firstName?: string,
  ) => Promise<{ needsConfirmation: boolean }>;
  confirm: (email: string, code: string) => Promise<void>;
  resend: (email: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  /** Start a password reset (emails a code). */
  forgotPassword: (email: string) => Promise<void>;
  /** Finish a password reset with the emailed code + new password. */
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Change the display name shown in the account menu. */
  updateFirstName: (firstName: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(syncEnabled ? 'loading' : 'anonymous');
  const [user, setUser] = useState<AuthUser | null>(null);

  // Restore any persisted session on first load.
  useEffect(() => {
    if (!syncEnabled) return;
    let cancelled = false;
    cognito.currentUser().then((u) => {
      if (cancelled) return;
      setUser(u);
      setStatus(u ? 'authenticated' : 'anonymous');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const u = await cognito.signIn(email, password);
    setUser(u);
    setStatus('authenticated');
  }, []);

  const updateFirstName = useCallback(async (firstName: string) => {
    await cognito.updateFirstName(firstName);
    setUser((u) => (u ? { ...u, firstName: firstName.trim() } : u));
  }, []);

  const signOut = useCallback(() => {
    cognito.signOut();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value: AuthContextValue = {
    enabled: syncEnabled,
    status,
    user,
    signUp: cognito.signUp,
    confirm: cognito.confirmSignUp,
    resend: cognito.resendCode,
    signIn,
    forgotPassword: cognito.forgotPassword,
    resetPassword: cognito.confirmForgotPassword,
    updateFirstName,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
